<script lang="ts">
  // Shared Settings body, used by BOTH the in-world overlay (SettingsDialog) and the full-screen
  // Free Duel route (Settings). Non-mod preferences live here; all theming/modpack editing (colours,
  // grid, backgrounds, audio, import/export) lives in the dedicated full-page Mod Editor, reached via
  // the button below — so it can preview against the real duel field.
  import { session, go } from "../stores/session.svelte.ts";
  import {
    settings, setPackAnimations, setSfxEnabled, setSfxVolume, setPerformanceMode,
    setReducedEffects, setAnimatedBackdrop, setRenderQuality, effectiveTier, type RenderQuality,
  } from "../stores/settings.svelte.ts";
  import { sfx } from "../stores/audio.svelte.ts";
  import AudioPack from "./AudioPack.svelte";

  const isWorld = $derived(session.mode === "world");
</script>

{#snippet sectionLabel(text: string)}
  <div class="px-1 pb-1 pt-1 font-display text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-cyan-dim">{text}</div>
{/snippet}

{#snippet toggleRow(title: string, desc: string, on: boolean, onToggle: () => void)}
  <button
    onclick={onToggle}
    class="flex w-full items-center justify-between gap-3 chamfer bg-void-2/40 px-4 py-3 text-left transition-colors hover:bg-surface-2/50"
    style="--c:9px"
    role="switch"
    aria-checked={on}
  >
    <span class="min-w-0">
      <span class="block font-display text-sm font-semibold text-ink">{title}</span>
      <span class="block text-xs text-ink-dim">{desc}</span>
    </span>
    <span class="relative h-5 w-9 shrink-0 rounded-full transition-colors duration-200 {on ? 'bg-cyan/70 glow-cyan' : 'bg-void/70 ring-1 ring-ink-dim/30'}">
      <span class="absolute top-0.5 size-4 rounded-full transition-all duration-200 {on ? 'left-[1.125rem] bg-void' : 'left-0.5 bg-ink'}"></span>
    </span>
  </button>
{/snippet}

{#snippet choiceRow(title: string, desc: string, options: { value: string; label: string }[], current: string, onPick: (v: string) => void)}
  <div class="chamfer bg-void-2/40 px-4 py-3" style="--c:9px">
    <span class="block font-display text-sm font-semibold text-ink">{title}</span>
    <span class="mb-2 block text-xs text-ink-dim">{desc}</span>
    <div class="flex gap-1.5" role="radiogroup" aria-label={title}>
      {#each options as opt}
        <button
          onclick={() => onPick(opt.value)}
          role="radio"
          aria-checked={current === opt.value}
          class="flex-1 chamfer px-2 py-1.5 font-display text-xs font-semibold uppercase tracking-[0.1em] transition-colors {current === opt.value ? 'bg-cyan/70 text-void glow-cyan' : 'bg-void/70 text-ink-dim ring-1 ring-ink-dim/30 hover:bg-surface-2/50'}"
          style="--c:7px"
        >{opt.label}</button>
      {/each}
    </div>
  </div>
{/snippet}

{#snippet sliderRow(title: string, value: number, onInput: (v: number) => void, onCommit?: () => void)}
  <div class="chamfer bg-void-2/40 px-4 py-3" style="--c:9px">
    <label class="block">
      <span class="mb-1.5 flex items-center justify-between">
        <span class="font-display text-sm font-semibold text-ink">{title}</span>
        <span class="text-xs tabular-nums text-ink-dim">{Math.round(value * 100)}%</span>
      </span>
      <input
        type="range" min="0" max="1" step="0.05" {value}
        class="w-full accent-cyan"
        oninput={(e) => onInput(+e.currentTarget.value)}
        onchange={() => onCommit?.()}
      />
    </label>
  </div>
{/snippet}

<div class="space-y-2 p-3 sm:p-4">
  {@render sectionLabel("Performance")}
  <p class="px-1 pb-1 text-xs text-ink-dim">On a weak machine or laptop battery? Turn these down for a smoother, cooler-running game.</p>
  {@render choiceRow(
    "Graphics quality",
    settings.renderQuality === "auto"
      ? `Auto matches your device and dials down if it can't keep up — currently running ${effectiveTier()}. Pick a level to lock it. Lower = fewer pixels + capped frame rate, easier on weak GPUs and fans.`
      : "Sharpness + frame rate of the 3D board and overworld. Lower is easier on weak GPUs and fans. Applies instantly.",
    [{ value: "auto", label: "Auto" }, { value: "low", label: "Low" }, { value: "medium", label: "Medium" }, { value: "high", label: "High" }],
    settings.renderQuality,
    (v) => setRenderQuality(v as RenderQuality),
  )}
  {@render toggleRow("Reduced effects", "Skip sparks, screen-shake, glows and idle animation. Keeps the game readable while cutting GPU/CPU work.", settings.reducedEffects, () => setReducedEffects(!settings.reducedEffects))}
  {@render toggleRow("Animated backdrop", "The moving cyber backdrop behind menus and duels. Off is a still image — lighter on the GPU.", settings.animatedBackdrop, () => setAnimatedBackdrop(!settings.animatedBackdrop))}
  {@render toggleRow("Performance mode", "The strongest option: use the flat 2D duel board instead of the 3D arena. Best for very weak machines.", settings.performanceMode, () => setPerformanceMode(!settings.performanceMode))}

  {@render sectionLabel("Duels")}
  {@render toggleRow("Duel sound effects", "Card placements, attacks, coin flips…", settings.sfxEnabled, () => { setSfxEnabled(!settings.sfxEnabled); if (settings.sfxEnabled) sfx("decide"); })}
  {@render sliderRow("SFX volume", settings.sfxVolume, (v) => setSfxVolume(v), () => sfx("cursor"))}

  {#if isWorld}
    {@render sectionLabel("World")}
    {@render toggleRow("Pack-opening animations", "Card flips + rarity bursts when opening packs", settings.packAnimations, () => setPackAnimations(!settings.packAnimations))}
  {/if}

  {@render sectionLabel("Texture Pack")}
  <button
    onclick={() => go("modeditor")}
    class="flex w-full items-center justify-between gap-3 chamfer bg-void-2/40 px-4 py-3 text-left transition-colors hover:bg-surface-2/50"
    style="--c:9px"
  >
    <span class="min-w-0">
      <span class="block font-display text-sm font-semibold text-ink">Texture Pack — your own look</span>
      <span class="block text-xs text-ink-dim">Re-skin the UI live with CSS: colours, fonts, backgrounds, playmat, zone alignment. Import / export a <code class="text-ink-dim/90">.zip</code> to share.</span>
    </span>
    <svg viewBox="0 0 24 24" class="size-4 shrink-0 text-cyan" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14" /><path d="m13 6 6 6-6 6" /></svg>
  </button>

  {@render sectionLabel("Audio Pack")}
  <p class="px-1 pb-1 text-xs text-ink-dim">Your own background audio — silent by default, follows the screen.</p>
  <AudioPack />
</div>
